import type { ErrorData } from 'hls.js'
import * as Sentry from '@sentry/vue'

import { isHlsUrl } from '@/composables/useHls'

export type TVideoPlaybackSentryKind
  = 'media_error'
    | 'hls_fatal'
    | 'hls_unsupported'
    | 'play_failed'

/** Where the failing decoder ran. Distinct from WebView `<video>` for hybrid Media3 filters. */
export type TVideoPlaybackSurface = 'webview' | 'native_media3'

/** Derived from `MediaError.code` for Sentry filters (low memory vs decode vs network). */
export type TVideoPlaybackMediaSubkind
  = 'decode'
    | 'network'
    | 'src_not_supported'
    | 'aborted'
    | 'unknown'

const MAX_TAG = 120

function mediaErrorSubkindFromCode(code: number | undefined): TVideoPlaybackMediaSubkind | undefined {
  if (code == null) {
    return undefined
  }
  if (code === MediaError.MEDIA_ERR_DECODE) {
    return 'decode'
  }
  if (code === MediaError.MEDIA_ERR_NETWORK) {
    return 'network'
  }
  if (code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
    return 'src_not_supported'
  }
  if (code === MediaError.MEDIA_ERR_ABORTED) {
    return 'aborted'
  }
  return 'unknown'
}

function truncateTag(value: string): string {
  return value.length <= MAX_TAG ? value : `${value.slice(0, MAX_TAG - 1)}…`
}

/** Benign when switching clips or tearing down the video element during `play()`. */
export function isBenignVideoPlayInterruptError(err: unknown): boolean {
  if (err == null || typeof err !== 'object') {
    return false
  }
  const name = 'name' in err && typeof (err as Error).name === 'string' ? (err as Error).name : ''
  if (name === 'AbortError') {
    return true
  }
  if (typeof DOMException !== 'undefined' && err instanceof DOMException && err.code === 20) {
    return true
  }
  const msg = 'message' in err && typeof (err as Error).message === 'string'
    ? (err as Error).message.toLowerCase()
    : ''
  return msg.includes('interrupted by a call to pause')
    || msg.includes('interrupted because the media was removed')
}

/** Avoid full URLs in Sentry (tokens, PII); keep enough to spot CDN vs blob vs manifest. */
function scrubUrlForSentry(raw: string | undefined): Record<string, unknown> {
  if (raw == null || raw === '') {
    return { src_kind: 'empty' }
  }
  if (raw.startsWith('blob:')) {
    return { src_kind: 'blob' }
  }
  if (isHlsUrl(raw) || raw.includes('.m3u8')) {
    return { src_kind: 'hls_manifest' }
  }
  try {
    const u = new URL(raw)
    const prefix = u.pathname.length > 80 ? `${u.pathname.slice(0, 77)}…` : u.pathname
    return { src_kind: 'remote', src_host: u.hostname, src_path_prefix: prefix }
  }
  catch {
    return { src_kind: 'opaque' }
  }
}

function hlsErrorExtras(data: ErrorData): Record<string, unknown> {
  const out: Record<string, unknown> = {
    hls_type: data.type,
    hls_details: data.details,
    hls_fatal: data.fatal
  }
  const err = data.error
  if (err != null) {
    out.hls_error_name = err.name
    out.hls_error_message = typeof err.message === 'string' ? err.message.slice(0, 500) : String(err)
  }
  if (typeof data.url === 'string') {
    Object.assign(out, scrubUrlForSentry(data.url))
  }
  const fragUrl = data.frag != null && typeof data.frag.url === 'string' ? data.frag.url : undefined
  if (fragUrl != null) {
    out.hls_frag = { ...scrubUrlForSentry(fragUrl), sn: data.frag?.sn }
  }
  if (data.response != null) {
    out.hls_response_code = data.response.code
    const text = data.response.text
    out.hls_response_text = typeof text === 'string' ? text.slice(0, 200) : undefined
  }
  return out
}

/**
 * Breadcrumb + event for video/HLS/play failures so Sentry Issues are searchable
 * (breadcrumbs alone only attach to other events).
 * Filter tags: `player_video_playback:true`, `video_playback_kind:*`.
 */
export function reportPlayerVideoPlaybackToSentry(params: {
  kind: TVideoPlaybackSentryKind
  /** Operator-facing string (matches SuppressedSectionNotice where applicable). */
  userVisibleReason?: string
  /** Current playback source (blob URL, remote URL, or manifest URL) — scrubbed before send. */
  playbackSrc?: string
  isHls?: boolean
  useHlsJs?: boolean
  capacityTier?: 'low' | 'mid' | 'high' | null
  /** From `watchLowMemory` (Tauri); tags `video_playback_low_memory`. */
  lowMemoryMode?: boolean
  mediaErrorCode?: number
  mediaErrorMessage?: string
  videoReadyState?: number
  videoNetworkState?: number
  hlsErrorData?: ErrorData
  /** For play_failed when captureException is used */
  caughtError?: unknown
  /** Default `webview`. Hybrid Media3 `onError` sets `native_media3`. */
  playbackSurface?: TVideoPlaybackSurface
  /** Media3 `PlaybackException.errorCodeName` (tag `video_playback_hybrid_error_code`). */
  hybridErrorCode?: string
}): void {
  const {
    kind,
    userVisibleReason,
    playbackSrc,
    isHls,
    useHlsJs,
    capacityTier,
    lowMemoryMode,
    mediaErrorCode,
    mediaErrorMessage,
    videoReadyState,
    videoNetworkState,
    hlsErrorData,
    caughtError,
    playbackSurface = 'webview',
    hybridErrorCode
  } = params

  const mediaSubkind = kind === 'media_error' ? mediaErrorSubkindFromCode(mediaErrorCode) : undefined

  const srcMeta = scrubUrlForSentry(playbackSrc)
  const breadcrumbData: Record<string, unknown> = {
    kind,
    userVisibleReason,
    ...srcMeta,
    isHls,
    useHlsJs,
    capacityTier,
    lowMemoryMode,
    mediaErrorCode,
    mediaSubkind,
    videoReadyState,
    videoNetworkState,
    playbackSurface,
    hybridErrorCode
  }
  if (hlsErrorData != null) {
    Object.assign(breadcrumbData, {
      hls_type: hlsErrorData.type,
      hls_details: hlsErrorData.details
    })
  }

  const tags: Record<string, string> = {
    player_video_playback: 'true',
    video_playback_kind: kind,
    video_playback_surface: playbackSurface,
    video_playback_low_memory: lowMemoryMode === true ? 'true' : 'false',
    ...(hybridErrorCode != null && hybridErrorCode !== ''
      ? { video_playback_hybrid_error_code: truncateTag(hybridErrorCode) }
      : {}),
    ...(capacityTier != null ? { video_playback_capacity_tier: String(capacityTier) } : {}),
    ...(mediaErrorCode != null
      ? { video_playback_media_error_code: String(mediaErrorCode) }
      : {}),
    ...(mediaSubkind != null
      ? { video_playback_media_subkind: mediaSubkind }
      : {}),
    ...(hlsErrorData != null
      ? {
          video_playback_hls_details: truncateTag(String(hlsErrorData.details)),
          video_playback_hls_type: truncateTag(String(hlsErrorData.type))
        }
      : {})
  }

  const extra: Record<string, unknown> = {
    userVisibleReason,
    ...srcMeta,
    isHls,
    useHlsJs,
    capacityTier,
    lowMemoryMode,
    mediaErrorCode,
    mediaErrorMessage,
    mediaSubkind,
    videoReadyState,
    videoNetworkState,
    playbackSurface,
    hybridErrorCode
  }
  if (hlsErrorData != null) {
    Object.assign(extra, hlsErrorExtras(hlsErrorData))
  }

  const fingerprint = [
    'player-video-playback',
    kind,
    playbackSurface,
    mediaErrorCode != null ? String(mediaErrorCode) : 'no_media_code',
    hybridErrorCode != null && hybridErrorCode !== '' ? hybridErrorCode : 'no_hybrid_code',
    hlsErrorData != null ? String(hlsErrorData.details) : 'no_hls_details',
    lowMemoryMode === true ? 'low_mem' : 'no_low_mem'
  ]

  try {
    Sentry.addBreadcrumb({
      category: 'player_video_playback',
      message: kind,
      level: 'warning',
      data: breadcrumbData
    })

    if (kind === 'play_failed' && caughtError != null) {
      if (isBenignVideoPlayInterruptError(caughtError)) {
        return
      }
      Sentry.captureException(caughtError, {
        tags,
        fingerprint,
        extra
      })
      return
    }

    Sentry.captureMessage('Player video playback failure', {
      level: 'warning',
      tags,
      fingerprint,
      extra
    })
  }
  catch {
    /* Sentry not initialized or unavailable */
  }
}
